/** QuickHouse — Menuiserie 3D d'une ouverture : dormant, vantaux (battants, coulissants,
 *  pleins ou vitrés), état ouvert/fermé, volets roulants ou à battants (pleins/persiennes).
 *  Double-clic : ouvre/ferme la menuiserie ; double-clic sur un volet : ouvre/ferme le volet.
 *  Rendu dans le repère local du mur (x le long, y vertical, z = épaisseur ; +z = extérieur). */
import { frameMaterial, glassMaterial, shutterMaterial } from './textures.js';
import { useProjectStore } from '../store/useProjectStore.js';

function editOpening(floorId, openingId, fn) {
  useProjectStore.getState().mutate((floors) => {
    const f = floors.find((x) => x.id === floorId);
    const o = f?.openings.find((x) => x.id === openingId);
    if (o) fn(o);
  });
}

const hoverCursor = {
  onPointerOver: (e) => { e.stopPropagation(); document.body.style.cursor = 'pointer'; },
  onPointerOut: () => { document.body.style.cursor = 'default'; },
};

const FW = 0.06; // largeur des montants de dormant (m)

/** Cadre rectangulaire (4 boîtes) centré en (0,0), dimensions extérieures w×h */
function Frame({ w, h, depth, material }) {
  return (
    <>
      <mesh position={[0, h / 2 - FW / 2, 0]} material={material} castShadow>
        <boxGeometry args={[w, FW, depth]} />
      </mesh>
      <mesh position={[0, -h / 2 + FW / 2, 0]} material={material} castShadow>
        <boxGeometry args={[w, FW, depth]} />
      </mesh>
      <mesh position={[-w / 2 + FW / 2, 0, 0]} material={material} castShadow>
        <boxGeometry args={[FW, h - 2 * FW, depth]} />
      </mesh>
      <mesh position={[w / 2 - FW / 2, 0, 0]} material={material} castShadow>
        <boxGeometry args={[FW, h - 2 * FW, depth]} />
      </mesh>
    </>
  );
}

/** Vantail battant : pivote autour de son bord gauche (hingeAt = x du gong) */
function SwingLeaf({ hingeX, width, height, solid, matFrame, angle }) {
  return (
    <group position={[hingeX, 0, 0]} rotation={[0, angle, 0]}>
      {solid ? (
        <mesh position={[width / 2, 0, 0]} material={matFrame} castShadow>
          <boxGeometry args={[width, height, 0.045]} />
        </mesh>
      ) : (
        // Vantail vitré : cadre (montants + traverses) autour d'un vitrage traversant —
        // rien de plein derrière le verre
        <>
          <mesh position={[width / 2, height / 2 - 0.04, 0]} material={matFrame} castShadow>
            <boxGeometry args={[width, 0.08, 0.045]} />
          </mesh>
          <mesh position={[width / 2, -height / 2 + 0.04, 0]} material={matFrame} castShadow>
            <boxGeometry args={[width, 0.08, 0.045]} />
          </mesh>
          <mesh position={[0.03, 0, 0]} material={matFrame} castShadow>
            <boxGeometry args={[0.06, Math.max(height - 0.16, 0.05), 0.045]} />
          </mesh>
          <mesh position={[width - 0.03, 0, 0]} material={matFrame} castShadow>
            <boxGeometry args={[0.06, Math.max(height - 0.16, 0.05), 0.045]} />
          </mesh>
          <mesh position={[width / 2, 0, 0]} material={glassMaterial()}>
            <boxGeometry args={[Math.max(width - 0.12, 0.05), Math.max(height - 0.16, 0.05), 0.02]} />
          </mesh>
        </>
      )}
    </group>
  );
}

export default function OpeningMesh({ opening: op, wallThickness, floorId, exteriorSide = 1 }) {
  if (!op.wallId || op.type === 'passage') return null;
  // Côté des volets : extérieur détecté (pièces) × correction manuelle éventuelle
  const shutterSide = exteriorSide * (op.meta?.shutterFlip ? -1 : 1);

  const toggleOpen = (e) => {
    e.stopPropagation();
    editOpening(floorId, op.id, (o) => { o.open = !o.open; });
    useProjectStore.getState().setStatus(op.open ? 'Menuiserie fermée' : 'Menuiserie ouverte');
  };
  const toggleShutter = (e) => {
    e.stopPropagation();
    editOpening(floorId, op.id, (o) => { o.shutterClosed = !o.shutterClosed; });
    useProjectStore.getState().setStatus(op.shutterClosed ? 'Volet ouvert' : 'Volet fermé');
  };

  const w = op.width / 100;
  const h = op.height / 100;
  const t = wallThickness / 100;
  const cx = op.offset / 100;
  const cy = (op.sillHeight + op.height / 2) / 100;
  const mat = frameMaterial(op.frameMaterial ?? 'pvc');
  const variant = op.variant ?? 'single';
  const solid = (op.type === 'door' && variant !== 'doubleGlazed') || op.type === 'garage';
  // Sens d'ouverture CONFORME AU PLAN 2D : l'arc du symbole est dessiné côté −z local ;
  // rotation Y positive = battant vers −z. flipV (⇅ du panneau) inverse.
  const dirZ = op.meta?.flipV ? -1 : 1;
  const hingeLeft = !op.meta?.flipH;
  const isSliding = ['sliding', 'bay', 'pocket'].includes(variant);
  const openAngle = 1.6 * dirZ; // le côté d'ouverture ne dépend que de flipV (⇅), comme en 2D
  const leafW = (n) => (w - 2 * FW) / n;
  const lh = h - 2 * FW;

  // ── Vantaux selon le type/variante ──
  let leaves = null;
  if (op.type === 'garage' && variant === 'roller') {
    const panelH = op.open ? Math.min(0.3, h - 0.1) : h - 0.08;
    leaves = (
      <>
        <mesh position={[0, h / 2 - 0.12, t / 2 - 0.1]} material={mat} castShadow>
          <boxGeometry args={[w, 0.2, 0.2]} />
        </mesh>
        <mesh position={[0, h / 2 - FW - panelH / 2, 0]} material={frameMaterial(op.frameMaterial ?? 'pvc', 'slats')} castShadow>
          <boxGeometry args={[w - 0.08, panelH, 0.03]} />
        </mesh>
      </>
    );
  } else if (isSliding) {
    // Deux panneaux coulissants sur deux plans ; ouvert = superposés (galandage : refoulés)
    const pw = w / 2 + 0.02;
    const x1 = op.open ? (variant === 'pocket' ? -w : -w / 4 - pw / 2 + 0.04) : -w / 4;
    const x2 = w / 4;
    leaves = (
      <>
        <group position={[x1, 0, -0.03]}>
          <SwingLeaf hingeX={-pw / 2} width={pw} height={lh} solid={solid} matFrame={mat} angle={0} />
        </group>
        <group position={[x2 - pw / 2, 0, 0.03]}>
          <SwingLeaf hingeX={0} width={pw} height={lh} solid={solid} matFrame={mat} angle={0} />
        </group>
      </>
    );
  } else if (['double', 'frenchDouble', 'doubleGlazed'].includes(variant)) {
    const lw = leafW(2);
    leaves = (
      <>
        <SwingLeaf hingeX={-w / 2 + FW} width={lw} height={lh} solid={solid} matFrame={mat}
          angle={op.open ? openAngle : 0} />
        <group scale={[-1, 1, 1]}>
          <SwingLeaf hingeX={-w / 2 + FW} width={lw} height={lh} solid={solid} matFrame={mat}
            angle={op.open ? openAngle : 0} />
        </group>
      </>
    );
  } else {
    const lw = leafW(1);
    leaves = (
      <group scale={[hingeLeft ? 1 : -1, 1, 1]}>
        <SwingLeaf hingeX={-w / 2 + FW} width={lw} height={lh} solid={solid} matFrame={mat}
          angle={op.open ? 1.6 * dirZ : 0} />
      </group>
    );
  }

  // ── Volets (sur la face extérieure détectée) ──
  let shutters = null;
  const zOut = shutterSide * (t / 2 + 0.03);
  const shColor = op.shutterColor ?? 'wood';
  if (op.shutter === 'roller') {
    const panelH = op.shutterClosed ? h - 0.04 : 0.18;
    shutters = (
      <>
        <mesh position={[0, h / 2 + 0.1, zOut + shutterSide * 0.04]} material={shutterMaterial(shColor)} castShadow>
          <boxGeometry args={[w + 0.16, 0.18, 0.16]} />
        </mesh>
        <mesh position={[0, h / 2 - panelH / 2, zOut]} material={shutterMaterial(shColor, 'slats')} castShadow>
          <boxGeometry args={[w + 0.04, panelH, 0.028]} />
        </mesh>
      </>
    );
  } else if (op.shutter === 'swing') {
    const overlay = op.shutterStyle === 'louvered' ? 'louver' : null;
    const smat = shutterMaterial(shColor, overlay);
    const sw = w / 2 + 0.04;
    // Fermé : les deux battants couvrent l'ouverture ; ouvert : rabattus contre la façade
    const angle = op.shutterClosed ? 0 : Math.PI * 0.98;
    shutters = (
      <group position={[0, 0, zOut]}>
        <group position={[-w / 2 - 0.02, 0, 0]} rotation={[0, -angle, 0]}>
          <mesh position={[sw / 2, 0, 0]} material={smat} castShadow>
            <boxGeometry args={[sw, h + 0.06, 0.032]} />
          </mesh>
        </group>
        <group position={[w / 2 + 0.02, 0, 0]} rotation={[0, angle, 0]}>
          <mesh position={[-sw / 2, 0, 0]} material={smat} castShadow>
            <boxGeometry args={[sw, h + 0.06, 0.032]} />
          </mesh>
        </group>
      </group>
    );
  }

  return (
    <group position={[cx, cy, 0]}>
      {/* Double-clic sur la menuiserie : ouvrir/fermer */}
      <group onDoubleClick={toggleOpen} {...hoverCursor}>
        <Frame w={w} h={h} depth={t * 0.45} material={mat} />
        {leaves}
      </group>
      {/* Double-clic sur le volet : ouvrir/fermer le volet */}
      {shutters && (
        <group onDoubleClick={toggleShutter} {...hoverCursor}>
          {shutters}
        </group>
      )}
    </group>
  );
}
